// JAPAN WEB CLINIC (Dawn) — the top page's motion (implementation plan v3 §8-2・§8-4), moved from the mock
// mocks-dawn/src/mock.js (版29: setupMarquee・setupAutoSlide・setupBars). Loaded with defer by the top's jwc- sections; it
// runs once however many of them include it, and again for a section that the theme editor renders anew.
//
// - The makers strip [data-jwc-marquee] (sections/jwc-makers.liquid; F-09・F-21): the logos flow sideways on their own.
//   The set of makers is repeated until it is as wide as the row, then doubled (the copies hidden from screen readers),
//   and the track moves by half its width: a seamless loop for any number of makers. A pause button (Dawn's slideshow
//   icons); a mouse over the row holds it (CSS). With reduced motion, or without this script, the row stands still.
// - The auto slide (F-28) of Dawn sliders whose cards do not all fit (phones and tablets): the sections whose keys are
//   listed in sections/jwc-top-bars.liquid (data-autoslide; voices = Dawn's multicolumn with 「スマホでスワイプ」 on) and
//   any [data-jwc-autoslide]. One card every few seconds, back to the first after the last. It stops while touched,
//   hovered or focused, off screen or in a background tab, with the pause button (added next to Dawn's arrows) and with
//   reduced motion.
// - The jump tabs [data-jwc-tabs] (sections/jwc-top-bars.liquid): fixed under the header once the first view has
//   scrolled past it; the section being read is marked (aria-current) and kept in view in the row. A tab whose section
//   is not on the page is hidden.
// - In-page links (#medicines, #voices, #faq …) also reach Dawn's sections, which have no id of their own: a name that
//   is no element's id is taken as the section's key in templates/index.json (its wrapper's id ends in "__<key>"). The
//   jump leaves room for the sticky header and the tabs, and moves the focus to the target.
// The bottom button is the shared bar of assets/jwc-common.js ([data-jwc-sticky]).
(() => {
  'use strict';
  const J = (window.JWC = window.JWC || {});
  if (J.top) return;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const A11Y = window.accessibilityStrings || {};
  const onReduceChange = (fn) => {
    if (reduce.addEventListener) reduce.addEventListener('change', fn);
    else if (reduce.addListener) reduce.addListener(fn);
  };

  // ---------------------------------------------------------------- targets: an id, or a section's key
  function findTarget(name) {
    if (!name) return null;
    const byId = document.getElementById(name);
    if (byId) return byId;
    const end = `__${name}`;
    return $$('.shopify-section[id]').find((section) => section.id.endsWith(end)) || null;
  }
  const nameOf = (hash) => {
    try {
      return decodeURIComponent(String(hash || '').replace(/^#/, '')).trim();
    } catch (e) {
      return '';
    }
  };
  const headerEl = () => $('.section-header');
  const headerBottom = () => Math.max(0, headerEl()?.getBoundingClientRect().bottom || 0);

  // ---------------------------------------------------------------- in-page links
  // room above a jump target: the sticky header (on the way down Dawn hides an "on-scroll-up" header) and the tabs
  // (shown below the first view; 4.4rem links and a rule while they are still hidden)
  function jumpTop(target) {
    const top = target.getBoundingClientRect().top + window.scrollY;
    let room = 12;
    const sticky = $('sticky-header');
    const header = headerEl();
    if (sticky && header && !(sticky.dataset.stickyType === 'on-scroll-up' && top > window.scrollY)) room += header.offsetHeight;
    const nav = $('[data-jwc-tabs]');
    const after = nav && tabs.get(nav)?.after;
    if (nav && after && after.isConnected && target !== after && top >= after.getBoundingClientRect().bottom + window.scrollY - 1) {
      room += nav.hidden ? 45 : nav.offsetHeight;
    }
    return Math.max(0, top - room);
  }
  function jump(target, smooth) {
    window.scrollTo({ top: jumpTop(target), behavior: smooth && !reduce.matches ? 'smooth' : 'auto' });
    if (!target.matches('a[href], button, input, select, textarea, [tabindex]')) {
      target.setAttribute('tabindex', '-1');
      target.classList.add('jwc-jump-target');
    }
    target.focus({ preventScroll: true });
  }
  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[href*="#"]');
    if (!link || !link.closest('#MainContent') || (link.target && link.target !== '_self')) return;
    let url;
    try {
      url = new URL(link.href, location.href);
    } catch (e) {
      return;
    }
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
    const name = nameOf(url.hash);
    const target = findTarget(name);
    if (!target) return;
    event.preventDefault();
    if (location.hash !== url.hash) history.pushState(null, '', url.hash);
    jump(target, true);
  }
  // an address with #<section key> (no element has that id, so the browser did not jump)
  function jumpToHash() {
    const name = nameOf(location.hash);
    if (!name || document.getElementById(name)) return;
    const target = findTarget(name);
    if (target) jump(target, false);
  }

  // ---------------------------------------------------------------- jump tabs
  const tabs = new Map();
  function setupTabs(nav) {
    if (tabs.has(nav)) return;
    tabs.set(nav, { links: $$('[data-jwc-tab]', nav), targets: [], after: null, shown: null });
    findTabTargets(nav);
  }
  function findTabTargets(nav) {
    const state = tabs.get(nav);
    state.targets = state.links.map((a) => findTarget(a.dataset.jwcTab));
    state.links.forEach((a, i) => {
      const item = a.closest('li');
      if (item) item.hidden = !state.targets[i];
    });
    state.after = findTarget(nav.dataset.showAfter);
  }
  function updateTabs() {
    tabsQueued = false;
    tabs.forEach((state, nav) => {
      if (!nav.isConnected) {
        tabs.delete(nav);
        return;
      }
      const top = headerBottom();
      const show = Boolean(state.after && state.after.isConnected && state.after.getBoundingClientRect().bottom < top);
      nav.hidden = !show;
      if (!show) return;
      nav.style.top = `${top}px`;
      document.documentElement.style.setProperty('--jwc-tabs-h', `${nav.offsetHeight}px`);
      // the current section: the lowest target whose top has reached the line under the tabs (a jump leaves 12px)
      const line = top + nav.offsetHeight + 16;
      let current = -1;
      let best = -Infinity;
      state.targets.forEach((target, i) => {
        if (!target || !target.isConnected) return;
        const t = target.getBoundingClientRect().top;
        if (t <= line && t > best) {
          best = t;
          current = i;
        }
      });
      state.links.forEach((a, i) => (i === current ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
      const active = state.links[current];
      if (active && state.shown !== active) {
        state.shown = active;
        // keep the current tab in view (the row scrolls only when it is cut off)
        const list = active.closest('ul');
        const lr = list.getBoundingClientRect();
        const ar = active.getBoundingClientRect();
        if (ar.right > lr.right - 16 || ar.left < lr.left) {
          list.scrollBy({ left: ar.left - lr.left - 16, behavior: reduce.matches ? 'auto' : 'smooth' });
        }
      }
    });
  }
  let tabsQueued = false;
  const queueTabs = () => {
    if (tabsQueued || !tabs.size) return;
    tabsQueued = true;
    requestAnimationFrame(updateTabs);
  };

  // ---------------------------------------------------------------- makers strip
  function setupMarquee(box) {
    if (box.jwcMarquee) return;
    const view = $('.jed__marquee-view', box);
    const track = $('.jed__marquee-track', box);
    const toggle = $('[data-jwc-marquee-toggle]', box);
    if (!view || !track) return;
    const originals = Array.from(track.children);
    if (!originals.length) return;
    box.jwcMarquee = true;
    const speed = Math.max(5, Number(box.dataset.speed) || 40); // px per second
    let lastWidth = 0;
    const layout = (force) => {
      const width = view.clientWidth;
      if (!force && width === lastWidth) return;
      lastWidth = width;
      $$('[data-jwc-marquee-clone]', track).forEach((node) => node.remove());
      box.classList.remove('is-running');
      if (toggle) toggle.hidden = true;
      // no pause button, no motion
      if (reduce.matches || !toggle) return;
      const one = originals.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0);
      if (!one) return;
      const copies = Math.ceil((width + 1) / one);
      for (let i = 1; i < copies * 2; i += 1) {
        originals.forEach((item) => {
          const copy = item.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('data-shopify-editor-block');
          copy.dataset.jwcMarqueeClone = '';
          track.append(copy);
        });
      }
      track.style.setProperty('--jed-marquee-duration', `${Math.max(8, Math.round((one * copies) / speed))}s`);
      box.classList.add('is-running');
      toggle.hidden = false;
    };
    if (toggle) {
      toggle.addEventListener('click', () => {
        const paused = box.classList.toggle('is-paused');
        const label = paused ? toggle.dataset.labelPlay || A11Y.playSlideshow : toggle.dataset.labelPause || A11Y.pauseSlideshow;
        if (label) toggle.setAttribute('aria-label', label);
      });
    }
    let timer = 0;
    const onResize = () => {
      if (!box.isConnected) {
        window.removeEventListener('resize', onResize);
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(() => layout(false), 150);
    };
    window.addEventListener('resize', onResize);
    onReduceChange(() => box.isConnected && layout(true));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => box.isConnected && layout(true));
    layout(true);
  }

  // ---------------------------------------------------------------- auto slide (voices)
  function setupAutoSlide(box, seconds) {
    if (!box || box.jwcAutoSlide) return;
    const list = $('.slider', box);
    if (!list) return; // 「スマホでスワイプ」 off: nothing moves
    let toggle = $('[data-jwc-autoslide-toggle]', box);
    if (!toggle) {
      const button = $('template[data-jwc-autoslide-template]')?.content.firstElementChild;
      if (!button) return; // no pause button, no motion
      toggle = button.cloneNode(true);
      const arrows = $('.slider-buttons', box);
      if (arrows) arrows.append(toggle);
      else {
        const bar = document.createElement('div');
        bar.className = 'jwc-autoslide__bar';
        bar.append(toggle);
        list.after(bar);
      }
    }
    box.jwcAutoSlide = true;
    box.classList.add('jwc-autoslide');
    let paused = false;
    let hold = false;
    let visible = true;
    let timer = 0;
    const every = Math.max(2, Number(seconds) || 4.5) * 1000;
    const scrolls = () => list.scrollWidth - list.clientWidth > 4;
    const refresh = () => {
      toggle.hidden = reduce.matches || !scrolls();
    };
    const next = () => {
      if (!box.isConnected) {
        clearInterval(timer);
        return;
      }
      if (paused || hold || !visible || document.hidden || reduce.matches || !scrolls()) return;
      const items = $$('.slider__slide', list).filter((item) => item.clientWidth > 0);
      const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : list.clientWidth;
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
      list.scrollTo({ left: atEnd ? 0 : list.scrollLeft + step, behavior: 'smooth' });
    };
    // a card always stays the full time on screen: the count starts again when the row comes into view, is let go or
    // is started again with the button
    const restart = () => {
      clearInterval(timer);
      timer = setInterval(next, every);
    };
    toggle.addEventListener('click', () => {
      paused = !paused;
      box.classList.toggle('is-paused', paused);
      const label = paused ? toggle.dataset.labelPlay || A11Y.playSlideshow : toggle.dataset.labelPause || A11Y.pauseSlideshow;
      if (label) toggle.setAttribute('aria-label', label);
      if (!paused) restart();
    });
    const holdOn = () => {
      hold = true;
    };
    const holdOff = () => {
      hold = false;
      restart();
    };
    ['pointerenter', 'touchstart'].forEach((type) => list.addEventListener(type, holdOn, { passive: true }));
    ['pointerleave', 'touchend', 'touchcancel'].forEach((type) => list.addEventListener(type, holdOff, { passive: true }));
    list.addEventListener('focusin', holdOn);
    list.addEventListener('focusout', holdOff);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        const now = entries[entries.length - 1].isIntersecting;
        if (now && !visible) restart();
        visible = now;
      }).observe(box);
    }
    if ('ResizeObserver' in window) new ResizeObserver(refresh).observe(list);
    window.addEventListener('resize', refresh);
    onReduceChange(refresh);
    refresh();
    restart();
  }
  function setupAutoSlides() {
    const config = $('[data-jwc-top-bars]');
    const seconds = Number(config?.dataset.autoslideSeconds) || 4.5;
    (config?.dataset.autoslide || '')
      .split(/[\s,、，]+/)
      .filter(Boolean)
      .forEach((name) => {
        const target = findTarget(name.replace(/^#/, ''));
        if (!target) return;
        setupAutoSlide(target.matches('slider-component') ? target : $('slider-component', target), seconds);
      });
    $$('[data-jwc-autoslide]').forEach((el) => {
      const box = el.matches('slider-component') ? el : $('slider-component', el) || el;
      setupAutoSlide(box, Number(el.dataset.jwcAutoslide) || seconds);
    });
  }

  // ---------------------------------------------------------------- start
  function init(root) {
    $$('[data-jwc-marquee]', root).forEach(setupMarquee);
    $$('[data-jwc-tabs]', root).forEach(setupTabs);
    setupAutoSlides();
    tabs.forEach((_, nav) => nav.isConnected && findTabTargets(nav));
    queueTabs();
  }
  function start() {
    init(document);
    window.addEventListener('scroll', queueTabs, { passive: true });
    window.addEventListener('resize', queueTabs);
    // Dawn slides an "on-scroll-up" header in and out (a transition of its top): follow it with the tabs
    headerEl()?.addEventListener('transitionend', queueTabs);
    document.addEventListener('click', onClick);
    window.addEventListener('hashchange', jumpToHash);
    if (document.readyState === 'complete') jumpToHash();
    else window.addEventListener('load', jumpToHash, { once: true });
    // theme editor: a section rendered anew gets its motion again; the shared bars follow the scroll even when the
    // bar was added after the page had loaded
    let barsHooked = false;
    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
      if (!barsHooked && typeof J.updateBars === 'function') {
        barsHooked = true;
        window.addEventListener('scroll', J.updateBars, { passive: true });
      }
      if (typeof J.updateBars === 'function') J.updateBars();
    });
    document.addEventListener('shopify:section:unload', () => {
      requestAnimationFrame(() => {
        tabs.forEach((_, nav) => nav.isConnected && findTabTargets(nav));
        queueTabs();
      });
    });
  }
  J.top = { findTarget, jump, init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
